/**
 * Datos sembrados del chatter y las notificaciones para el modo vista previa.
 *
 * Van en su propio archivo porque, a diferencia del resto de la vista previa,
 * esto tiene estado: publicar, editar, reaccionar y marcar como leido cambian
 * lo que se devuelve despues. Sin eso no se puede probar la pantalla.
 *
 * Las notificaciones NO se escriben a mano: se derivan de los comentarios, con
 * la misma regla que aplica el backend. Escribir las dos cosas por separado ya
 * dio dos veces un tablero que decia una cosa y un detalle que decia otra.
 */
import { Comentario, Mencionable, Notificacion } from '../core/interfaces/collab.interface';

/** Quien mira. En la vista previa no hay sesion de Microsoft. */
const YO = { id: 1, nombre: 'Facundo Aguirre', correo: 'faguirre@vippinn.com' };

const GENTE: Mencionable[] = [
  { id: 1, nombre: 'Facundo Aguirre', correo: 'faguirre@vippinn.com', del_proyecto: true },
  { id: 2, nombre: 'Silvina Álvarez', correo: 'salvarez@vippinn.com', del_proyecto: true },
  { id: 3, nombre: 'Juan Pérez', correo: 'jperez@vippinn.com', del_proyecto: true },
  { id: 4, nombre: 'Elena Ruiz', correo: 'eruiz@vippinn.com', del_proyecto: false },
  { id: 5, nombre: 'Martín Sosa', correo: 'msosa@vippinn.com', del_proyecto: false },
  { id: 6, nombre: 'Paula Giménez', correo: 'pgimenez@vippinn.com', del_proyecto: false }
];

/** Hace n horas, en ISO. Las fechas fijas envejecen mal en una demo. */
function haceHoras(n: number): string {
  return new Date(Date.now() - n * 3600_000).toISOString();
}

let proximoId = 100;

/** Los comentarios por proyecto. Es la unica fuente: todo lo demas se deriva. */
const COMENTARIOS: Record<string, Comentario[]> = {
  'PRJ-001': [
    {
      id: 1,
      parent_id: null,
      autor_id: 2,
      autor: 'Silvina Álvarez',
      body: 'Cerré la certificación de agosto. @Facundo Aguirre quedó pendiente revisar las horas de Trakkinn, que llegaron después del corte.',
      fecha: haceHoras(30),
      editado: false,
      menciones: [{ id: 1, nombre: 'Facundo Aguirre' }],
      reacciones: [{ emoji: '👍', cantidad: 2, personas: ['Juan Pérez', 'Elena Ruiz'], mia: false }],
      puede_editar: false
    },
    {
      id: 2,
      parent_id: 1,
      autor_id: 3,
      autor: 'Juan Pérez',
      body: 'Las cargo hoy. Son 34 horas de dos consultores.',
      fecha: haceHoras(28),
      editado: false,
      menciones: [],
      reacciones: [],
      puede_editar: false
    },
    {
      id: 3,
      parent_id: null,
      autor_id: 4,
      autor: 'Elena Ruiz',
      body: 'Ojo con la provisión de septiembre: el proveedor todavía no mandó la factura.',
      fecha: haceHoras(5),
      editado: true,
      menciones: [],
      reacciones: [],
      puede_editar: false
    }
  ],
  'PRJ-003': [
    {
      id: 10,
      parent_id: null,
      autor_id: 5,
      autor: 'Martín Sosa',
      body: '@Facundo Aguirre este proyecto quedó con margen negativo dos meses seguidos, ¿lo miramos?',
      fecha: haceHoras(2),
      editado: false,
      menciones: [{ id: 1, nombre: 'Facundo Aguirre' }],
      reacciones: [],
      puede_editar: false
    }
  ]
};

const NOMBRE_PROYECTO: Record<string, string> = {
  'PRJ-001': 'CER',
  'PRJ-003': 'Monitor'
};

/** Las que se marcaron como leidas en esta sesion */
const LEIDAS = new Set<number>();

/**
 * Deriva las notificaciones de los comentarios, con la regla del backend:
 * mencion, comentario general de otro, o respuesta en un hilo donde uno
 * escribio. Nunca de lo propio.
 */
function notificaciones(): Notificacion[] {
  const lista: Notificacion[] = [];

  for (const [code, comentarios] of Object.entries(COMENTARIOS)) {
    const misHilos = new Set(
      comentarios.filter((c) => c.autor_id === YO.id).map((c) => c.parent_id ?? c.id)
    );

    for (const c of comentarios) {
      if (c.autor_id === YO.id) continue;

      const mencionado = c.menciones.some((m) => m.id === YO.id);
      const enMiHilo = c.parent_id !== null && misHilos.has(c.parent_id);

      let tipo: Notificacion['tipo'] | null = null;
      if (mencionado) tipo = 'mention';
      else if (c.parent_id === null) tipo = 'comment';
      else if (enMiHilo) tipo = 'reply';

      if (!tipo) continue;

      lista.push({
        id: c.id,
        tipo,
        emoji: null,
        leida: LEIDAS.has(c.id),
        fecha: c.fecha,
        actor: c.autor,
        proyecto_code: code,
        proyecto_nombre: NOMBRE_PROYECTO[code] ?? null,
        comentario_id: c.id,
        extracto: c.body.length > 140 ? c.body.slice(0, 140) + '…' : c.body
      });
    }
  }

  return lista.sort((a, b) => b.fecha.localeCompare(a.fecha));
}

/** Alterna una reaccion sobre un comentario, como hace el backend */
function alternarReaccion(c: Comentario, emoji: string): void {
  const actual = c.reacciones.find((r) => r.emoji === emoji);

  if (actual?.mia) {
    actual.cantidad -= 1;
    actual.mia = false;
    actual.personas = actual.personas.filter((p) => p !== YO.nombre);
    if (actual.cantidad === 0) c.reacciones = c.reacciones.filter((r) => r.emoji !== emoji);
    return;
  }

  if (actual) {
    actual.cantidad += 1;
    actual.mia = true;
    actual.personas = [...actual.personas, YO.nombre];
    return;
  }

  c.reacciones = [...c.reacciones, { emoji, cantidad: 1, personas: [YO.nombre], mia: true }];
}

/**
 * Resuelve las rutas del chatter. Devuelve null si la url no es de las suyas,
 * para que el interceptor siga con la siguiente.
 */
export function respuestaCollab(
  metodo: string,
  ruta: string,
  params: URLSearchParams,
  cuerpo: unknown
): unknown | null {
  /* ----- notificaciones ----- */
  if (/\/api\/notifications\/?$/.test(ruta) && metodo === 'GET') {
    const items = notificaciones();
    return { success: true, sin_leer: items.filter((n) => !n.leida).length, data: items };
  }

  if (/\/api\/notifications\/read-all$/.test(ruta)) {
    notificaciones().forEach((n) => LEIDAS.add(n.id));
    return { success: true };
  }

  const leer = ruta.match(/\/api\/notifications\/(\d+)\/read$/);
  if (leer) {
    LEIDAS.add(Number(leer[1]));
    return { success: true };
  }

  /* ----- chatter ----- */
  const mencionables = ruta.match(/\/api\/project\/([^/]+)\/mencionables$/);
  if (mencionables) {
    const buscado = (params.get('buscar') ?? '').toLowerCase();

    const gente = GENTE.filter(
      (g) =>
        !buscado ||
        g.nombre.toLowerCase().includes(buscado) ||
        (g.correo ?? '').toLowerCase().includes(buscado)
    ).sort(
      (a, b) => Number(b.del_proyecto) - Number(a.del_proyecto) || a.nombre.localeCompare(b.nombre)
    );

    return { success: true, data: gente.slice(0, 20) };
  }

  const reaccion = ruta.match(/\/api\/project\/([^/]+)\/comments\/(\d+)\/reactions$/);
  if (reaccion) {
    const lista = COMENTARIOS[decodeURIComponent(reaccion[1])] ?? [];
    const c = lista.find((x) => x.id === Number(reaccion[2]));
    if (!c) return { success: false, message: 'Comentario no encontrado' };

    alternarReaccion(c, (cuerpo as { emoji: string }).emoji);
    return { success: true, activa: c.reacciones.some((r) => r.emoji === (cuerpo as { emoji: string }).emoji && r.mia), data: c };
  }

  const uno = ruta.match(/\/api\/project\/([^/]+)\/comments\/(\d+)$/);
  if (uno && metodo === 'PATCH') {
    const lista = COMENTARIOS[decodeURIComponent(uno[1])] ?? [];
    const c = lista.find((x) => x.id === Number(uno[2]));
    if (!c) return { success: false, message: 'Comentario no encontrado' };

    c.body = (cuerpo as { body: string }).body;
    c.editado = true;
    return { success: true, data: c };
  }

  const hilo = ruta.match(/\/api\/project\/([^/]+)\/comments$/);
  if (hilo) {
    const code = decodeURIComponent(hilo[1]);
    COMENTARIOS[code] ??= [];

    if (metodo === 'GET') {
      return { success: true, data: COMENTARIOS[code] };
    }

    const datos = cuerpo as { body: string; menciones: number[]; parent_id: number | null };

    const nuevo: Comentario = {
      id: proximoId++,
      parent_id: datos.parent_id ?? null,
      autor_id: YO.id,
      autor: YO.nombre,
      body: datos.body,
      fecha: new Date().toISOString(),
      editado: false,
      menciones: GENTE.filter((g) => (datos.menciones ?? []).includes(g.id)).map((g) => ({
        id: g.id,
        nombre: g.nombre
      })),
      reacciones: [],
      // Recien publicado: entra en la ventana de los 15 minutos
      puede_editar: true
    };

    COMENTARIOS[code] = [...COMENTARIOS[code], nuevo];

    return { success: true, data: nuevo };
  }

  return null;
}
